import { format, parseISO } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Season } from '@/types/season';
import type { SeasonTaskStats } from '@/services/dashboard-service';
import type { SeasonHuntStats } from '@/utils/countdown-utils';
import { daysUntilOpener, seasonStatus } from '@/utils/countdown-utils';
import { usePropertyStore } from '@/stores/property-store';
import { WEAPON_LABELS } from '@/utils/season-utils';

const LONG_PRESS_DURATION = 750; // ms
const PULSE_DURATION = 120; // ms

interface CountdownWidgetProps {
  season: Season;
  taskStats: SeasonTaskStats;
  variant?: 'hero' | 'compact';
  /** Sit/harvest totals for the season — drives in-season sit count and post-season summary. */
  huntStats?: SeasonHuntStats;
}

/** Task progress text only — no visual bar. */
function TaskProgress({ taskStats }: { taskStats: SeasonTaskStats }) {
  const label =
    taskStats.total > 0
      ? `${taskStats.completed}/${taskStats.total} tasks complete`
      : 'Add tasks to track your prep';

  return (
    <p className="text-[10px] text-[#9a9590] mt-1" data-testid="task-progress">
      {label}
    </p>
  );
}

/** Subtitle above the numeral — season name + weapon type in caps */
function buildSubtitle(season: Season): string {
  const year = season.openerDate.slice(0, 4);
  const state = season.state.toUpperCase();
  const weapon = WEAPON_LABELS[season.weaponType].toUpperCase();
  // E.g. "2026 WI GUN DEER" — use the season name minus the year prefix if present
  return `${year} ${state} ${weapon}`;
}

// ---------------------------------------------------------------------------
// START HUNT tactical switch with 750ms long-press
// ---------------------------------------------------------------------------

interface StartHuntButtonProps {
  navigateUrl: string;
}

function StartHuntButton({ navigateUrl }: StartHuntButtonProps) {
  const navigate = useNavigate();
  const [isPressing, setIsPressing] = useState(false);
  const [fillPct, setFillPct] = useState(0);
  const [isPulsing, setIsPulsing] = useState(false);

  const rafRef = useRef<number | null>(null);
  const pressStartRef = useRef<number | null>(null);
  const completedRef = useRef(false);

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cancelPress = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pressStartRef.current = null;
    setIsPressing(false);
    // Animate fill back to 0 in ~150ms
    setFillPct(0);
    completedRef.current = false;
  }, []);

  const handlePointerDown = useCallback(() => {
    if (prefersReducedMotion) return;

    completedRef.current = false;
    pressStartRef.current = performance.now();
    setIsPressing(true);
    setFillPct(0);

    function tick() {
      const start = pressStartRef.current;
      if (start === null) return;

      const elapsed = performance.now() - start;
      const pct = Math.min(100, (elapsed / LONG_PRESS_DURATION) * 100);
      setFillPct(pct);

      if (elapsed >= LONG_PRESS_DURATION) {
        // Long press complete
        completedRef.current = true;
        rafRef.current = null;
        pressStartRef.current = null;
        setIsPressing(false);
        setFillPct(100);
        setIsPulsing(true);
        // Vibrate on supported devices
        navigator.vibrate?.(50);
        // Brief pulse then navigate
        setTimeout(() => {
          setIsPulsing(false);
          navigate(navigateUrl);
        }, PULSE_DURATION);
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [prefersReducedMotion, navigate, navigateUrl]);

  const handlePointerUp = useCallback(() => {
    if (prefersReducedMotion) return;
    if (!completedRef.current) {
      cancelPress();
    }
  }, [prefersReducedMotion, cancelPress]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  if (prefersReducedMotion) {
    return (
      <button
        type="button"
        onClick={() => navigate(navigateUrl)}
        className="relative w-full px-5 py-5 bg-ember-500 rounded-lg shadow-ember border-t-[0.5px] border-ember-400/20 flex items-center justify-center gap-3 mt-3 overflow-hidden"
        data-testid="start-hunt-button"
        data-variant="tactical-switch"
        aria-label="Start a hunt"
      >
        <span className="font-sans font-bold text-[14px] tracking-widest uppercase text-white relative z-10">
          START HUNT
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="relative z-10"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    );
  }

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className={[
        'relative w-full px-5 py-5 bg-ember-500 rounded-lg shadow-ember border-t-[0.5px] border-ember-400/20 flex items-center justify-center gap-3 mt-3 overflow-hidden select-none',
        isPulsing ? 'scale-[1.02]' : '',
      ].join(' ')}
      data-testid="start-hunt-button"
      data-variant="tactical-switch"
      data-pressing={isPressing ? 'true' : 'false'}
      data-fill-pct={Math.round(fillPct)}
      aria-label="Start a hunt"
      style={{ transition: isPulsing ? `transform ${PULSE_DURATION}ms ease-out` : undefined }}
    >
      {/* Fill overlay — rAF-driven width, linear */}
      <div
        aria-hidden
        className="absolute inset-0 bg-white/10 origin-left pointer-events-none"
        style={{ width: `${fillPct}%`, transition: fillPct === 0 ? 'width 150ms linear' : 'none' }}
      />
      <span className="font-sans font-bold text-[14px] tracking-widest uppercase text-white relative z-10">
        START HUNT
      </span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="relative z-10"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

export function CountdownWidget({
  season,
  taskStats,
  variant = 'compact',
  huntStats,
}: CountdownWidgetProps) {
  const navigate = useNavigate();
  // M-07: source the primary property from the property store instead of UserPreferences.
  const { properties } = usePropertyStore();
  const primaryPropertyId = properties.find((p) => p.isPrimary)?.id ?? null;

  const days = daysUntilOpener(season.openerDate);
  const status = seasonStatus(season);

  const isEnded = status === 'ended';
  const isOpenerToday = days === 0 && !isEnded;
  const isInSeason = days < 0 && !isEnded;
  const isNear = status === 'upcoming' && days <= 14;

  const isHero = variant === 'hero';
  const openerFormatted = format(parseISO(season.openerDate), 'MMM d, yyyy');
  const subtitle = buildSubtitle(season);

  // ── START HUNT (opener day only) ─────────────────────────────────────────
  if (isOpenerToday) {
    const startHuntUrl = primaryPropertyId
      ? `/hunt/new?seasonId=${season.id}&propertyId=${primaryPropertyId}`
      : `/hunt/new?seasonId=${season.id}`;

    return (
      <div
        className="bg-[#252320] border border-[#c8651a] rounded-lg overflow-hidden border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
        data-testid="countdown-widget"
        data-urgency="today"
        data-countdown-season-id={season.id}
        {...(isHero && { 'data-variant': 'hero' })}
      >
        {/* Subtitle */}
        <div className="px-5 pt-5 text-center">
          <p
            className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
            data-testid="countdown-subtitle"
          >
            {subtitle}
          </p>
        </div>

        <div className="px-5">
          <StartHuntButton navigateUrl={startHuntUrl} />
        </div>

        <div className="px-5 pb-4 pt-2">
          <p
            className="text-[12px] font-medium tracking-wide text-[#c4c0bb] text-center"
            data-testid="countdown-label"
          >
            SEASON OPENS TODAY
          </p>
          <TaskProgress taskStats={taskStats} />
        </div>
      </div>
    );
  }

  // ── SEASON ACTIVE (opener passed, season ongoing) ─────────────────────────
  if (isInSeason) {
    const dayNum = Math.abs(days) + 1;
    const sitSummary =
      huntStats && huntStats.totalSits > 0
        ? `${huntStats.totalSits} sit${huntStats.totalSits !== 1 ? 's' : ''} · ${huntStats.morningSits} morning · ${huntStats.eveningSits} evening`
        : null;

    return (
      <div
        className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg overflow-hidden px-5 py-4 text-center"
        data-testid="countdown-widget"
        data-urgency="in-season"
        data-countdown-season-id={season.id}
        {...(isHero && { 'data-variant': 'hero' })}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
          data-testid="countdown-subtitle"
        >
          SEASON ACTIVE
        </p>
        <p
          className="font-display text-[64px] leading-none text-[#c8651a] mt-2"
          data-testid="countdown-numeral"
          data-urgency="in-season"
          {...(isHero && { 'data-hero-numeral': 'true' })}
        >
          Day {dayNum}
        </p>
        <p
          className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-1"
          data-testid="countdown-label"
        >
          {openerFormatted} · {season.name}
        </p>
        {sitSummary && (
          <p
            className="text-[10px] text-[#9a9590] mt-1"
            data-testid="sit-summary"
          >
            {sitSummary}
          </p>
        )}
      </div>
    );
  }

  // ── Standard countdown (ended or upcoming) ───────────────────────────────

  if (isEnded) {
    const summaryNode = huntStats ? (
      <p
        className="text-[10px] text-[#9a9590] mt-1"
        data-testid="season-summary"
      >
        {`${huntStats.totalSits} sit${huntStats.totalSits !== 1 ? 's' : ''} · ${huntStats.totalHarvests} harvest${huntStats.totalHarvests !== 1 ? 's' : ''} · ${huntStats.daysAfield} day${huntStats.daysAfield !== 1 ? 's' : ''} afield`}
      </p>
    ) : null;

    return (
      <div
        className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg px-5 py-4 text-center"
        data-testid="countdown-widget"
        data-urgency="off-season"
        data-countdown-season-id={season.id}
        {...(isHero && { 'data-variant': 'hero' })}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
          data-testid="countdown-subtitle"
        >
          {subtitle}
        </p>
        <p
          className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-2"
          data-testid="countdown-label"
        >
          SEASON COMPLETE
        </p>
        {summaryNode}
        <TaskProgress taskStats={taskStats} />
      </div>
    );
  }

  // Upcoming seasons
  const urgency: 'normal' | 'near' = isNear ? 'near' : 'normal';

  // navigate is used in the SEASON ACTIVE and SEASON COMPLETE branches via inline onClick
  void navigate;

  return (
    <div
      className={[
        'bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg px-5 py-4 text-center',
        isNear ? 'border-[#c8651a]' : '',
      ].join(' ')}
      data-testid="countdown-widget"
      data-urgency={urgency}
      data-countdown-season-id={season.id}
      {...(isHero && { 'data-variant': 'hero' })}
    >
      {/* Subtitle: 10px bold caps muted */}
      <p
        className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
        data-testid="countdown-subtitle"
      >
        {subtitle}
      </p>

      {/* Numeral: 64px DM Serif Display ember */}
      <p
        className="font-display text-[64px] leading-none text-[#c8651a] mt-2"
        data-testid="countdown-numeral"
        data-urgency={urgency}
        {...(isHero && { 'data-hero-numeral': 'true' })}
      >
        {days}
      </p>

      {/* Label: 12px medium secondary */}
      <p
        className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-1"
        data-testid="countdown-label"
      >
        DAYS TO OPENER
      </p>

      <TaskProgress taskStats={taskStats} />
    </div>
  );
}
